import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { styles } from '../../../styles/themes/style';
import SvgFragment from '../../../fragments/SvgFragment';
import { AuthMethodButton, AuthSkipLink } from '../../authentication/authFormCommon';

export const SYNC_NETWORK_PAGE_SUBTITLE =
    'LeadDelta needs your permission to sync the following into your workspace.';

export interface ISyncNetworkCardProps {
    onSync: () => void;
    onContinueWithoutSync: () => void;
    onOpenPermissions?: () => void;
    syncing?: boolean;
}

const SyncNetworkCard: FunctionComponent<ISyncNetworkCardProps> = ({
    onSync,
    onContinueWithoutSync,
    onOpenPermissions,
    syncing = false,
}) => (
    <SyncCard>
        <ContentSection>
            <FeatureCards>
                <FeatureCard>
                    <IconWrapper>
                        <SvgFragment type="peopleBlue" />
                    </IconWrapper>
                    <CardContent>
                        <CardTitleRow>
                            <CardTitle>Sync your connections</CardTitle>
                            <RequiredBadge>Required</RequiredBadge>
                        </CardTitleRow>
                        <CardText>
                            We import your 1st-degree LinkedIn connections so you can tag, filter, note, and organize them in
                            LeadDelta and on a LinkedIn sidebar.
                        </CardText>
                    </CardContent>
                </FeatureCard>

                <FeatureCard>
                    <IconWrapper>
                        <SvgFragment type="inboxBlue" />
                    </IconWrapper>
                    <CardContent>
                        <CardTitleRow>
                            <CardTitle>Sync your inbox</CardTitle>
                            <RequiredBadge>Required</RequiredBadge>
                        </CardTitleRow>
                        <CardText>
                            We import your LinkedIn messages so you can run your inbox like a CRM — tag, pin, archive, add notes
                            and reminders, and use keyboard shortcuts to hit inbox zero faster.
                        </CardText>
                    </CardContent>
                </FeatureCard>
            </FeatureCards>

            <PrivacyRow>
                <SvgFragment type="lock" />
                <PrivacyText>
                    You can disconnect LeadDelta or remove your data at any time.{' '}
                    {onOpenPermissions && (
                        <PermissionLink type="button" onClick={onOpenPermissions}>
                            See exactly what LeadDelta can access.
                        </PermissionLink>
                    )}
                </PrivacyText>
            </PrivacyRow>
        </ContentSection>

        <Actions>
            <AuthMethodButton
                $type="blue"
                disabled={syncing}
                isLoading={syncing}
                loadingText="Starting sync..."
                tooltipMessage=""
                onClickHandler={onSync}
            >
                Sync my network
            </AuthMethodButton>
            <AuthSkipLink type="button" disabled={syncing} onClick={onContinueWithoutSync}>
                Continue without sync
            </AuthSkipLink>
        </Actions>
    </SyncCard>
);

const SyncCard = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 24px;
    gap: 24px;
    width: 100%;
    border: 1px solid ${styles.colors.black200};
    border-radius: 12px;
    background: ${styles.colors.white};
`;

const ContentSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
`;

const FeatureCards = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
`;

const FeatureCard = styled.div`
    border: 1px solid ${styles.colors.black200};
    border-radius: 10px;
    padding: 16px;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 10px;
`;

const IconWrapper = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    margin-top: 2px;

    & .svg {
        width: 20px;
        height: 20px;
    }
`;

const CardContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;
    min-width: 0;
`;

const CardTitleRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
`;

const CardTitle = styled.span`
    font-family: NunitoSans;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    color: ${styles.colors.black400};
`;

const RequiredBadge = styled.span`
    display: flex;
    align-items: center;
    height: 18px;
    padding: 0 8px;
    border-radius: 12px;
    font-family: NunitoSans;
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
    color: ${styles.colors.black500};
    background-color: ${styles.colors.black200};
    white-space: nowrap;
`;

const CardText = styled.p`
    margin: 0;
    font-family: NunitoSans;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    color: ${styles.colors.black400};
`;

const PrivacyRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 6px;

    & .svg {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        margin-top: 2px;
    }
`;

const PrivacyText = styled.p`
    margin: 0;
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: 400;
    line-height: 19px;
    color: ${styles.colors.black400};
`;

const PermissionLink = styled.button`
    background: none;
    border: none;
    padding: 0;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    color: ${styles.colors.primary600};
    cursor: pointer;
`;

const Actions = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
`;

export default SyncNetworkCard;
